import type { TooltipOptions } from 'highcharts';
import Highcharts from 'highcharts';
import { HighchartPoint } from './types/tooltip';
import { formatMessage } from '@/utils/i18n';

export const MAX_ITEMS_IN_CHART_TOOLTIP = 10;

export const getSharedTooltip = (tooltipFormatter: () => string): TooltipOptions => ({
  className: 'chart-tooltip',
  useHTML: true,
  shared: true,
  formatter() {
    return tooltipFormatter.call(this);
  },
  backgroundColor: '#ffffff',
  borderColor: '#E5E5E5',
  borderRadius: 6,
  padding: 16,
  shape: 'callout',
  split: true,
  shadow: false,
  followPointer: true,
  animation: false,
  hideDelay: 0,
  style: {
    color: '#000000',
    borderRadius: 4,
  },
});

export function getChartTooltipHeader({ x, timeFormatter }: { x: number; timeFormatter?: Highcharts.Time }) {
  // Note : we prefer using the timeFormatter from the chart instance if available as it is already localized
  // and configured with the right timezone.
  // Fallback to Highcharts.dateFormat if not available (should not happen in practice)
  return `<span class="chart-tooltip-date-header">${timeFormatter ? timeFormatter.dateFormat('%A, %b %e, %Y', x) : Highcharts.dateFormat('%A, %b %e, %Y', x)}</span>`;
}

export function getTooltipPointsToDisplay(points: HighchartPoint[], maxItems = MAX_ITEMS_IN_CHART_TOOLTIP) {
  return points
    .slice() // to avoid mutating the original array
    .sort((a, b) => Number(b.y) - Number(a.y))
    .slice(0, maxItems);
}

function getItemReturnsTemplate(point: HighchartPoint) {
  const { tooltip } = point.series.options;
  const valuePrefix = tooltip?.valuePrefix ?? '';
  const valueSuffix = tooltip?.valueSuffix ?? '';
  return `<tr class="chart-tooltip-item">
    <td class="chart-tooltip-icon">
      <i class="icon-icon-circle holding-icon" style="color: ${point.series.color};"></i>
    </td>
    <td class="chart-tooltip-item-name pe-4" data-private data-dd-privacy="mask">${point.series.name} ${valuePrefix}:</td>
    <td class="chart-tooltip-item-value text-end">${Number(point.y).toFixed(2)} ${valueSuffix}</td>
  </tr>`;
}

export function getCommonTooltipBody(points: HighchartPoint[], maxItems = MAX_ITEMS_IN_CHART_TOOLTIP) {
  const body = `<table>${getTooltipPointsToDisplay(points, maxItems).map(getItemReturnsTemplate).join('')}</table>`;
  const overflowNotice =
    points.length > maxItems ? `<div class="chart-tooltip-overflow-notice">+ ${points.length - maxItems} ${formatMessage('Common.More')}</div>` : '';
  return `<div class="chart-tooltip-body">${body}${overflowNotice}</div>`;
}

export function commonPlotTooltipFormatter(this: Highcharts.TooltipFormatterContextObject & { points: HighchartPoint[] }) {
  const header = getChartTooltipHeader({ x: this.x as number, timeFormatter: this.points?.[0].series?.chart?.time });
  return header + getCommonTooltipBody(this.points);
}
